import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../contexts/AppProvider';
import { ChartNetwork, RefreshCw, AlertCircle } from 'lucide-react';
import { SingleStatComponent } from '../dependencies/StatsComponents';
import API_CONFIG from '../../config/api';
import { snakeToTitle } from '../../utils/strParsing';
import MultiLineCharts from '../dependencies/RechartMultiLineComponent';
import MultiBarCharts from '../dependencies/RechartMultiBarCharts';


const BASE_URL = API_CONFIG.BASE_URL;


export default function SurveyPerformanceMetrics({ featureName }) {
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	const [statsData, setStatsData] = useState(null);
	const { tenantInfos, t } = useApp();

	const fetchFeatureData = useCallback(async () => {
		setIsLoading(true);
		setError(null);

		try {
			const tenant_id = tenantInfos.interfaceId;
			const access_token = tenantInfos.accessToken;
			const api_url = `${BASE_URL}/cui_portal/${tenant_id}/${access_token}/features/get_data/${featureName}`;
			
			const result = await fetch(api_url);
			
			if (result.status === 404) {
				setError({ statusCode: 404, detail: "Feature data not found" });
				return;
			}
			if (result.status === 401) {
				setError({ statusCode: 401, detail: "You are not authorized to access this feature.\nPlease contact your administrator" });
				return;
			}
			if (!result.ok) {
				setError({ statusCode: 500, detail: "Unexpected error happened" });
				return;
			}
			
			const response = await result.json();
			
			if (response.success && response.data) {
				setStatsData(response.data);
				setError(null);
			} else if (response.error) {
				setError({ statusCode: response.error.status_code, detail: response.error.detail || response.error.message });
			} else {
				setError({ statusCode: 422, detail: "Unexpected data structure" });
			}
		} catch (err) {
			console.error("Error fetching feature data:", err);
			setError({ statusCode: 500, detail: "Failed to fetch feature data" });
		} finally {
			setIsLoading(false);
		}
	}, [featureName, tenantInfos]);

	useEffect(() => {
		fetchFeatureData();
	}, [fetchFeatureData]);

	const handleRefreshButton = () => {
		fetchFeatureData();
	};


	const singleStats = Object.entries(statsData?.single_stats || {}).map(
		([key, value]) => ({
		  key: value === -1 ? "Temporarly unavailable" : key,
		  value,
		})
	);

	// Loading state
	if (isLoading) {
		return (
			<div className="max-w-7xl mx-auto px-6 py-12">
			<div className="flex items-center justify-center min-h-[400px]">
				<div className="text-center">
				<div 
					className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4"
					style={{ borderColor: 'var(--color-primary)' }}
				/>
				<p style={{ color: 'var(--color-muted)' }}>Loading metrics data...</p>
				</div>
			</div>
			</div>
		);
	}
	// Error state
	if (error) {
	return (
		<div className="max-w-7xl mx-auto px-6 py-12">
		<div className="flex items-center justify-center min-h-[400px]">
			<div 
			className="p-6 border"
			style={{ 
				backgroundColor: 'color-mix(in srgb, red 10%, var(--color-surface))',
				borderColor: 'color-mix(in srgb, red 30%, transparent)'
			}}
			>
			<div className="flex items-start gap-3">
				<AlertCircle className="h-6 w-6 flex-shrink-0" style={{ color: 'red' }} />
				<div>
				<h3 className="font-semibold mb-1" style={{ color: 'var(--color-text)' }}>
					Error {error.statusCode}
				</h3>
				<p style={{ color: 'var(--color-text)', whiteSpace: 'pre-line' }}>
					{error.detail}
				</p>
				</div>
			</div>
			</div>
		</div>
		</div>
	);
	}

	return (
		<div className="mx-auto px-6 py-8">
			{/* Header */}
			<div className="flex items-center justify-between mb-6">
				<div className="flex items-end gap-2">
					<ChartNetwork className="h-8 w-8 mb-1" style={{ color: 'var(--color-primary)' }} />
					<h1 className="text-3xl font-bold" style={{ color: 'var(--color-primary)' }}>
						{t("surveyPerformanceTitle")}
					</h1>
				</div>
				<button
					onClick={handleRefreshButton}
					className="px-4 py-2 border transition-all duration-200 hover:shadow-md"
					style={{
						backgroundColor: 'var(--color-surface)',
						borderColor: 'color-mix(in srgb, var(--color-muted) 50%, transparent)',
						color: 'var(--color-text)'
					}}
					onMouseEnter={(e) => {
						e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--color-primary) 5%, var(--color-surface))';
					}}
					onMouseLeave={(e) => {
						e.currentTarget.style.backgroundColor = 'var(--color-surface)';
					}}
					>
					<div className="flex items-center gap-2">
						<RefreshCw className="h-4 w-4" />
						<span className="text-sm font-medium">{t("refresh")}</span>
					</div>
				</button>
			</div>
			<div
				className="grid gap-4 mb-6"
				style={{
					display: 'grid',
					gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
				}}
				>
				{singleStats.map((stat) => (
					<SingleStatComponent
					key={stat.key}
					value={stat.value}
					description={snakeToTitle(stat.key)}
					/>
				))}
			</div>
			{/* Charts section */}
			<div
				className="shadow-lg overflow-y-auto"
				style={{
				borderColor: "color-mix(in srgb, var(--color-muted) 50%, transparent)",
				maxHeight: "850px", // ~ two charts tall
				padding: "0.5rem",
				scrollbarWidth: "thin",
				scrollbarColor:
					"color-mix(in srgb, var(--color-primary) 30%, var(--color-muted)) transparent",
				}}
			>
				<div className="flex flex-col gap-10">
					<MultiLineCharts chartData={statsData.multi_lines_charts} />
					<MultiBarCharts chartData={statsData.bar_charts} />
				</div>
			</div>
		</div>
	
);
}